import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, Heart, Briefcase, Users, ShieldCheck, ExternalLink, Smartphone } from "lucide-react";
import { api } from "../../lib/api";
import { setViewAs, getViewAs } from "../../lib/viewAs";

function GuestLinks({ w, invite }) {
  const links = w ? [
    ["Wedding website", `/site/${w.slug}`], ["Social feed & stories", `/site/${w.slug}/social`], ["Guest photo upload", `/site/${w.slug}/upload`],
    ...(invite ? [["Animated invite (as guest)", `/invite/${w.slug}/${invite}?preview=1`]] : []), ["Guest portal login", "/guest/login"], ["Public social hub", "/social"],
  ] : [];
  return (
    <div className="space-y-1.5">
      {links.map(([l, to]) => <a key={l} href={to} target="_blank" rel="noreferrer" className="flex items-center justify-between text-sm border border-[var(--line)] rounded-xl px-3 py-2 hover:border-[var(--gold)] transition-colors" data-testid={`views-guest-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{l}<ExternalLink className="w-3.5 h-3.5 text-[var(--muted)]" /></a>)}
    </div>
  );
}

export default function AdminViewsPage() {
  const nav = useNavigate();
  const [w, setW] = useState(null);
  const [invite, setInvite] = useState(null);
  const [cur, setCur] = useState(getViewAs());
  useEffect(() => {
    api.get("/weddings/mine").then((r) => setW(r.data)).catch(() => {});
    api.get("/weddings/mine/invites").then((r) => setInvite(r.data.invites[0]?.invite_id || null)).catch(() => {});
  }, []);
  const go = (v) => { setViewAs(v); setCur(v); nav("/dashboard"); };
  const card = "bg-white border border-[var(--line)] rounded-2xl p-5 flex flex-col";
  const Role = ({ v, icon: Icon, title, desc }) => (
    <div className={`${card} ${cur === v ? "ring-2 ring-[var(--gold)]" : ""}`} data-testid={`views-card-${v}`}>
      <Icon className="w-6 h-6 text-[var(--gold)] mb-3" /><p className="font-serif text-2xl">{title}</p><p className="text-sm text-[var(--muted)] mt-1 mb-4 flex-1">{desc}</p>
      <button onClick={() => go(v)} className={cur === v ? "btn-outline-gold text-xs" : "btn-gold text-xs"} data-testid={`views-switch-${v}`}><Eye className="w-3 h-3" /> {cur === v ? "Viewing now" : "View as " + title.toLowerCase()}</button>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="admin-views-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Admin</p>
        <h1 className="font-serif font-light text-4xl">All Views</h1>
        <p className="text-[var(--muted)] mt-2 text-sm">See the platform the way each person does. Switching only changes what you see; you keep full admin access.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Role v="admin" icon={ShieldCheck} title="Admin" desc="Your full console: every tool, report and setting." />
        <Role v="host" icon={Heart} title="Wedding host" desc="The couple's dashboard: planning, guests, invites, memories and payouts." />
        <Role v="planner" icon={Briefcase} title="Client planner" desc="The planner and vendor workspace: clients, contacts, reports and events." />
      </div>
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <div className={card} data-testid="views-card-guest">
          <Users className="w-6 h-6 text-[var(--gold)] mb-3" /><p className="font-serif text-2xl">Guest view</p>
          <p className="text-sm text-[var(--muted)] mt-1 mb-4">Everything a guest opens{w ? ` for ${w.couple_names}` : ""}. Links open in a new tab.</p>
          {w ? <GuestLinks w={w} invite={invite} /> : <p className="text-sm text-[var(--muted)]">Create a wedding to preview guest pages. <Link to="/onboarding" className="underline">Start</Link></p>}
        </div>
        {w && (
          <div data-testid="views-phone">
            <p className="text-xs text-[var(--muted)] mb-2 flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Live guest preview</p>
            <div className="w-[320px] h-[620px] rounded-[2.2rem] border-[10px] border-[#111] shadow-2xl overflow-hidden mx-auto"><iframe title="Guest view" src={`/site/${w.slug}`} className="w-full h-full" data-testid="views-phone-frame" /></div>
          </div>
        )}
      </div>
    </div>
  );
}
